import { Tooltip, TooltipContent, TooltipTrigger } from '@/design-system/tooltip';
import { cn } from '@/lib/utils';
import { useLayoutEffect, useRef, useState } from 'react';

export const TruncatedTooltipValue = ({
  content,
  className,
  tooltipClassName,
}: {
  content: string;
  className?: string;
  tooltipClassName?: string;
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const check = () => {
    const el = triggerRef.current;
    if (!el) return;

    const overflowing = el.scrollWidth > el.clientWidth + 1;
    setIsTruncated((previous) => (previous === overflowing ? previous : overflowing));
  };

  useLayoutEffect(() => {
    let rafId = 0;

    const measure = () => {
      const el = triggerRef.current;
      if (!el || el.clientWidth === 0) {
        rafId = window.requestAnimationFrame(measure);
        return;
      }

      check();
    };

    measure();

    if (typeof ResizeObserver === 'undefined')
      return () => {
        if (rafId) window.cancelAnimationFrame(rafId);
      };

    const observer = new ResizeObserver(check);
    if (triggerRef.current) observer.observe(triggerRef.current);

    return () => {
      observer.disconnect();
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [content]);

  const span = (
    <span ref={triggerRef} className={cn('block w-full truncate', className)} onMouseEnter={check} onFocus={check}>
      {content}
    </span>
  );

  if (!isTruncated) {
    return span;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{span}</TooltipTrigger>
      <TooltipContent className="h-auto max-w-xl whitespace-normal break-words">
        <span className={cn('block', tooltipClassName)}>{content}</span>
      </TooltipContent>
    </Tooltip>
  );
};
